import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.testing.1riskint.com/';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Request interceptor — attach token if present
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('portalToken');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — handle 401
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config._skipAuthRedirect) {
      localStorage.removeItem('portalUser');
      localStorage.removeItem('portalToken');
      window.location.href = import.meta.env.BASE_URL + 'login';
    }
    return Promise.reject(error);
  }
);

/**
 * Resolve the legacy app URL for a given apiName.
 * Matches portalv2 DataService.determineURL().
 */
export function determineURL(apiName) {
  const name = apiName.toLowerCase();
  if (name === 'testapi' || name.includes('lab') || name === 'stagingpeakapi') {
    return 'https://testing.1risk.info/';
  }
  if (name === 'justinapi') {
    return 'http://localhost/';
  }
  return 'https://my.1risk.net/';
}

export const authAPI = {
  /**
   * Authenticate via Basic Auth.
   * GET /{apiName}/auth/basic
   */
  login: (apiName, username, password) => {
    return apiClient.get(`${apiName}/auth/basic`, {
      headers: {
        Authorization: 'Basic ' + btoa(username + ':' + password),
      },
      _skipAuthRedirect: true,
    });
  },

  /**
   * Load user preferences and app list after login.
   * POST /{apiName}/rest/application/load
   */
  loadApplication: (apiName, username, password) => {
    const baseUrl = determineURL(apiName);
    return axios.post(
      `${baseUrl}${apiName}/rest/application/load`,
      {
        applicationId: 'onerisk-app-sso',
        deviceName: 'ionic',
        devicePlatform: 'ionic',
        deviceType: 'ionic',
        deviceUUID: 'ionic',
        osName: 'ionic',
        registeredApp: 'Checklist',
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Authorization: 'Basic ' + btoa(username + ':' + password),
        },
      }
    );
  },

  /**
   * Request a password reset email.
   * POST /{apiName}/auth/request-password-reset
   */
  requestPasswordReset: (apiName, username) => {
    return apiClient.post(`${apiName}/auth/request-password-reset`, { username });
  },

  /**
   * Submit a new password using a reset token.
   * POST /{apiName}/auth/reset-password
   */
  resetPassword: (apiName, username, token, newPassword) => {
    return apiClient.post(`${apiName}/auth/reset-password`, {
      token,
      apiName,
      credentials: btoa(username + ':' + newPassword),
    });
  },

  /**
   * Validate whether a reset token has expired.
   * POST /{apiName}/auth/reset-expired
   */
  validateResetToken: (apiName, username, token) => {
    return apiClient.post(`${apiName}/auth/reset-expired`, {
      username,
      apiName,
      token,
    });
  },

  /**
   * Clear local auth state. Preserves cachedApi for quick re-login.
   */
  logout: () => {
    const cachedApi = localStorage.getItem('portalCachedApi');
    localStorage.removeItem('portalUser');
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalActiveApi');
    localStorage.removeItem('portalOperation');
    if (cachedApi) {
      localStorage.setItem('portalCachedApi', cachedApi);
    }
  },
};

export default apiClient;
